/*
 * FleetLocationPage — the POC screen: the fleet on a map, and the credentials that make it
 * work, on one page.
 *
 * They are together on purpose. The thing being demonstrated is the RELATIONSHIP between
 * them: remove a credential and the locations stop, add it back and they return. Two
 * separate screens would make that a story someone has to narrate instead of something the
 * audience watches happen.
 */

import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import BackButton from '../shared/BackButton';
import { TEXT_SECONDARY } from './fleetLocationStyles';
import FleetMapView from './FleetMapView';
import ScaniaConnectionsView from './ScaniaConnectionsView';

class FleetLocationPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = { organizations: [], loaded: false };
    this.mapRef = React.createRef();
  }

  // ADMINS ONLY (FLSW.NAV.3). The map is about to appear in the navigation for every user of
  // an enabled customer, and this section pastes and deletes live Scania secrets. The API
  // already refuses a non-admin; this removes the surface, so a driver is not shown a control
  // that exists only to reject them.
  isAdminOf(organizationId) {
    const organization = this.state.organizations.find((org) => org.id === organizationId);
    return Boolean(organization && organization.user_role === 'admin');
  }

  componentDidMount() {
    fetch('/uiapi/1/auth_user/', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((resp) => (resp.ok ? resp.json() : { organizations: [] }))
      .then((user) => {
        const organizations = user && user.organizations ? user.organizations : [];
        this.setState({
          organizations,
          loaded: true,
        });
      })
      .catch(() => this.setState({ loaded: true }));
  }

  refreshMap() {
    // THE WHOLE MAP, from its configuration down. Adding the first credential changes
    // whether the browser may have the map key at all, so refetching only the vehicle list
    // would leave a customer looking at a list where a map should now be.
    //
    // The map is wrapped by the translation HOC, so the ref points at the wrapper. Reaching
    // through it defensively rather than assuming either shape: if the wrapper ever stops
    // being there, an assumption about `getWrappedInstance` turns the demo's whole point --
    // add a credential, watch the fleet appear -- into a page that quietly does nothing.
    const node = this.mapRef.current;
    if (!node) {
      return;
    }
    const view = node.getWrappedInstance ? node.getWrappedInstance() : node;
    if (view && view.load) {
      view.load();
    }
  }

  render() {
    const { organizations, loaded } = this.state;
    const { t, selectedOrganizationId } = this.props;
    const organization = organizations.find(org => String(org.id) === String(selectedOrganizationId));
    const organizationId = organization ? organization.id : null;
    return (
      <div>
        {/* THE ONLY WAY OUT. This route is not linked from any navigation -- reaching it
            means typing the URL -- so without this a fleet manager who opens it is
            stranded on a screen with no exit but the browser's back button. Points at
            the organisation's garage, which is where the fleet portal starts. */}
        {organizationId && (
          <div style={{ padding: '16px 16px 0 16px' }}>
            <BackButton to={'/organization_garage/' + organizationId} />
          </div>
        )}
        {/* HISTORY GOES DOWN. This page IS a Route, so it has one; the map did not, and
            fell back to a full document navigation on every pin or row click --
            discarding in-flight position reads and restarting the whole application. */}
        <FleetMapView ref={this.mapRef} history={this.props.history} />
        {loaded && organizationId && this.isAdminOf(organizationId) && (
          // KEYED BY ORGANISATION, so switching remounts the screen rather than leaving it
          // showing the previous fleet's credentials. Without the key, Remove acted on a row
          // id from the organisation the administrator had just navigated away from —
          // deleting a real credential, from the wrong fleet, with no warning.
          <ScaniaConnectionsView
            key={organizationId}
            organizationId={organizationId}
            organizationName={organization.name}
            onChanged={() => this.refreshMap()}
          />
        )}
        {loaded && !organizations.some(org => org.user_role === 'admin') && (
          <p style={{ padding: 16, color: TEXT_SECONDARY, fontSize: 15 }}>
            {t('notAdministrator')}
          </p>
        )}
      </div>
    );
  }
}

export default connect(state => ({
  selectedOrganizationId: state.organization.selectedOrganization,
}))(translate('FleetLocation')(FleetLocationPage));
